<!-- eslint-disable max-len -->
<template>
  <div id='project-budget' class='view-contents'>
    <UnavailablePageModal
      v-if="project"
      pageName="Budget"
      :project="project"
      :unallowedStatuses="unallowedStatuses"
    />
    <b-modal id="openWorkOrdersModal" title="Open Work Orders" size="lg" ok-only>
      <p>One or more Work Orders are open, and therefore this new budget cannot be approved.</p>
    </b-modal>
    <b-modal id="openWorkOrderBillsModal" title="Open Work Order Bills" size="lg" ok-only>
      <p>One or more Work Orders contain Bills that are open, and therefore this new budget cannot be approved.</p>
    </b-modal>
    <b-modal
      id="insufficientRemainingBudget"
      title="Insufficient Remaining Budget"
      size="lg" ok-only>
      <p>The amount being requested in this annual budget exceeds the remaining funds for this Project, which is calculated as 'Total Approved Project Budget' minus 'Spent', and therefore cannot be approved.</p>
    </b-modal>
    <b-alert show dismissible
      v-if="!projectBudget.projectBudgetYears || projectBudget.projectBudgetYears.length === 0">
      To get started, add a fiscal year for which to build a budget.
    </b-alert>
    <div>
      <ProjectBudgetDecrease />
    </div>
    <b-tabs pills content-class="mt-3" nav-wrapper-class="budget-tabs">
      <b-tab v-if='showProjectExpenditureSummary' title="Expenditure Summary" active>
        <ExpenditureSummary />
      </b-tab>
      <b-tab title="Funding Breakdown">
        <FundingBreakdown />
      </b-tab>
      <b-tab title="Budget History" v-if="canSeeProjectHistory">
        <ProjectBudgetHistory />
      </b-tab>
    </b-tabs>
    <div class='row'>
    </div>
    <div class="row">
      <div class='col-6 mid-page-section-header'>
        <div class='normal-title'>
          Annual Budgets
        </div>
      </div>
      <div class='col-6 mid-page-section-header'>
        <!-- eslint-disable-next-line -->
        <b-button
          v-if="canAddAnnualBudget && project.startDate && project.endDate"
          pill size="sm" variant="primary" class="float-right-button action-button"
          @click="openAddYearModal"
        >
          <span>Add Fiscal Year</span>
        </b-button>
        <b-button
          v-if='canAddAnnualBudget && (!project.startDate || !project.endDate)'
          pill size="sm" variant="primary" class="float-right-button action-button"
          disabled
        >
          <span v-b-tooltip.hover title='Invalid Insightly Project Start/End Date'>Add Fiscal Year</span>
        </b-button>
      </div>
    </div>
    <b-modal id="addYearModal" title="Select Fiscal Year" size="md">
      <div class="row details-row">
        <div class="col-3">
          <label for="product-select">Fiscal Year</label>
        </div>
        <div class="col-9">
          <LoadingSpinner v-if='loadingData'
            message ='Loading Fiscal Years' size = '1x'>
          </LoadingSpinner>
          <b-form-select
            v-else
            v-model="yearToAdd"
            :options="possibleYears">
          </b-form-select>
        </div>
      </div>
      <template v-slot:modal-footer>
        <b-button
          size="sm"
          variant="danger"
          @click="cancelAddYear"
          class='management-plan-dialog-no'>
          Cancel
        </b-button>
        <b-button
          size="sm"
          variant="success"
          :disabled="addFiscalYearDisabled"
          @click="addFiscalYear"
          class='management-plan-dialog-yes'>
          Add Year
        </b-button>
      </template>
    </b-modal>
    <div
      class="details-row"
      v-for="budgetYear in projectBudget.projectBudgetYears"
      :key="budgetYear.year">
      <AnnualBudget :budgetYear="budgetYear"/>
    </div>
  </div>
</template>
<script>
/* eslint-disable max-len, padded-blocks */
import { mapState } from 'vuex';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import AnnualBudget from '@/components/projects/AnnualBudget.vue';
import ExpenditureSummary from '@/components/projects/ExpenditureSummary.vue';
import FundingBreakdown from '@/components/projects/FundingBreakdown.vue';
import ProjectBudgetDecrease from '@/components/projects/ProjectBudgetDecrease.vue';
import ProjectBudgetHistory from '@/components/projects/ProjectBudgetHistory.vue';
import ProjectBudgetHelperExpenditureSummary from '@/mixins/projectBudgetHelperExpenditureSummary';
import ProjectTotalsCalculator from '@/mixins/projectTotalsCalculator';
import ProjectBudgetMembershipManager from '@/mixins/projectBudgetMembershipManager';
import ProjectApprovalMembershipManager from '@/mixins/projectApprovalMembershipManager';
import ProjectWorkOrderBillingManager from '@/mixins/projectWorkOrderBillingManager';
import UnavailablePageModal from '@/components/common/UnavailablePageModal.vue';
import ProjectSchemaHelper from '@/mixins/projectSchemaHelper';
import FiscalYear from '@/mixins/fiscalYear';

export default {
  name: 'ProjectBudget',
  components: {
    AnnualBudget,
    ExpenditureSummary,
    FundingBreakdown,
    LoadingSpinner,
    ProjectBudgetDecrease,
    ProjectBudgetHistory,
    UnavailablePageModal,
  },
  mixins: [
    ProjectApprovalMembershipManager,
    ProjectBudgetHelperExpenditureSummary,
    ProjectTotalsCalculator,
    ProjectBudgetMembershipManager,
    ProjectWorkOrderBillingManager,
    ProjectSchemaHelper,
    FiscalYear,
  ],
  computed: {
    ...mapState(['config']),
    ...mapState('Project', {
      successMessage: (state) => state.decreaseSuccessMessage,
      currentPriceLists: (state) => state.currentPriceLists,
      errorMessage: (state) => state.errorMessage,
      fundingSources: (state) => state.fundingSources,
      fundingSourcesLoading: (state) => state.fundingSourcesLoading,
      fundsPerFiscalYear: (state) => state.fundsPerFiscalYear.funds,
      hasOpenWorkOrders: (state) => state.hasOpenWorkOrders,
      hasOpenWorkOrderBills: (state) => state.hasOpenWorkOrderBills,
      currentPriceListsLoading: (state) => state.currentPriceListsLoading,
      priceLists: (state) => state.priceLists,
      products: (state) => state.products,
      project: (state) => state.project,
      projectBudget: (state) => state.projectBudget,
      projectBudgetYearSnapshots: (state) => state.projectBudgetYearSnapshots,
      projectIncentivePayments: (state) => state.projectIncentivePayments,
      projectSnapshot: (state) => state.projectSnapshot,
      projectSnapshots: (state) => state.projectSnapshots,
      currentProjectSnapshots: (state) => state.currentProjectSnapshots,
      projectStatus: (state) => state.projectStatus,
      projectWorkorders: (e) => e.projectWorkorders,
      projectWorkorderLineItemFundingSources: (e) => e.projectWorkorderLineItemFundingSources,
      projectWorkorderBills: (e) => e.projectWorkorderBills,
      incentiveTypes: (state) => state.incentiveTypes,
    }),
    ...mapState('Contact', {
      contactBriefs: (e) => e.contactBriefs,
    }),
    ...mapState('Opportunity', {
      opportunityBriefs: (e) => e.workorderOpportunityBriefs,
    }),
    ...mapState('Organization', {
      organizationBriefs: (e) => e.organizationBriefs,
    }),
    addFiscalYearDisabled() {
      return this.loadingData || this.possibleYears.length < 1;
    },
  },
  data() {
    return {
      yearToAdd: null,
      possibleYears: [],
      loadingData: false,
      validForm: false,
      unallowedStatuses: [],
      projectWorkorderAddIsVisible: false,
    };
  },
  watch: {
    /* eslint-disable-next-line */
    projectStatus: function (projectStatus) {
      /* eslint-disable-next-line */
      if (projectStatus && projectStatus === this.config.projectOverviewPage.submitStatus.Approved) {
        (async () => {
          await this.loadProjectBudgetSummaryData();
          await this.loadProjectTotalsSummaryData();
        })();
      }
    },
  },
  async mounted() {
    this.loadingData = true;
    await this.$store.dispatch('fetchConfiguration');
    this.unallowedStatuses = [
      this.config.projectOverviewPage.submitStatus.ConceptRejected,
      this.config.projectOverviewPage.submitStatus.Draft,
    ];
    Promise.all([
      this.$store.dispatch('Project/setProjectBudgetSummaryDataLoaded', false),
      this.$store.dispatch('Project/getProject', parseInt(this.$route.params.id, 10)),
      this.$store.dispatch('Project/fetchPriceLists'),
      this.$store.dispatch('Project/fetchProducts'),
      this.$store.dispatch('Project/getIncentiveTypes'),
      this.$store.dispatch('Project/fetchFundingSources'),
    ]).then(async () => {
      await this.$store.dispatch('Project/fetchProjectBudget', parseInt(this.$route.params.id, 10));
      await this.$store.dispatch('Project/getProjectIncentivePayments', this.project.id);
      await this.$store.dispatch('Contact/getContactBriefs');
      await this.loadProjectBudgetSummaryData();
      await this.loadProjectTotalsSummaryData();
      await this.loadWorkorderLookups();
      this.loadingData = false;
      this.cleanUpSelectedYear();
      this.setUpPossibleYears();
      this.$store.dispatch('Project/setProjectBudgetSummaryDataLoaded', true);
    });

  },
  methods: {
    openAddYearModal() {
      this.$bvModal.show('addYearModal');
    },
    cancelAddYear() {
      this.$bvModal.hide('addYearModal');
      this.cleanUpSelectedYear();
    },
    cleanUpSelectedYear() {
      // the next time we open up the modal, we want to default
      // to the next available year
      this.yearToAdd = null;
      let testYear = this.getFiscalYear(this.project.startDate);
      while (!this.yearToAdd) {
        if (this.projectBudget.projectBudgetYears.map((pby) => pby.year).includes(testYear)) {
          testYear += 1;
        } else {
          this.yearToAdd = testYear;
        }
      }
    },
    initializeYear(yearValue) {
      return {
        year: yearValue,
        projectBudgetLineItems: [],
        annualBudgetIncreases: [],
        status: 'Draft',
      };
    },
    addFiscalYear() {
      this.$bvModal.hide('addYearModal');
      this.projectBudget.projectBudgetYears.push(this.initializeYear(this.yearToAdd));
      this.projectBudget.projectBudgetYears = this.projectBudget.projectBudgetYears
        .sort((a, b) => a.year < b.year);
      this.saveBudget();
      this.cleanUpSelectedYear();
      this.setUpPossibleYears();
    },
    setUpPossibleYears() {
      const startFiscalYear = this.getFiscalYear(this.project.startDate);
      const endFiscalYear = this.getFiscalYear(this.project.endDate);
      this.possibleYears = [];
      /* eslint-disable-next-line */
      for(let i = startFiscalYear; i <= endFiscalYear; i++) {
        if (!this.projectBudget.projectBudgetYears.map((pby) => pby.year).includes(i)) {
          this.possibleYears.push({
            value: i,
            text: i,
            disabled: false,
          });
        }
      }
    },
    submitBudget() {
      this.saveBudget();
    },
    saveBudget() {
      this.$store.dispatch('Project/saveProjectBudget');
    },
    async loadWorkorderLookups() {
      await this.$store.dispatch('Opportunity/getWorkorderOpportunityBriefs');
      await this.$store.dispatch('Organization/getOrganizationBriefs');
    },
  },
};
</script>
